"use client";

import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information";
import { useFilterPositions } from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import { useAccountAnalytics } from "@/vybe-trader-client/trade-history/hooks/useAccountAnalytics";
import type { AnalyticsScope } from "@/vybe-trader-client/trade-history/accountAnalytics";
import { DATE_FILTER_LABELS } from "@/vybe-trader-client/trade-history/stores/useFilterStore";
import AnalyticsInfoCard from "./AnalyticsInfoCard";
import AnalyticsChartCard from "./AnalyticsChartCard";
import AnalyticsAdvancedCard from "./AnalyticsAdvancedCard";
import AnalyticsActivityCard from "./AnalyticsActivityCard";
import AnalyticsMonthlyCard from "./AnalyticsMonthlyCard";
import { fmtDayMonth } from "./analyticsShared";

export interface HistoryAnalyticsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The History tab's live filter config at the moment the menu opened. */
  initialScope: AnalyticsScope;
}

/**
 * Account Analytics (proposal 70) — a Myfxbook-grade report computed 100%
 * client-side from the already-synced stores. Near-fullscreen Dialog, same
 * host pattern as HistoryDetailsDialog; content (and therefore the analytics
 * pass) only mounts while the modal is open.
 */
export default function HistoryAnalyticsModal({
  open,
  onOpenChange,
  initialScope,
}: HistoryAnalyticsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-none flex-col gap-0 overflow-hidden p-0">
        {open && <AnalyticsModalBody initialScope={initialScope} />}
      </DialogContent>
    </Dialog>
  );
}

function scopeDateLabel(scope: AnalyticsScope): string {
  if (scope.dateFilter === "all") return "All history";
  if (scope.dateFilter === "custom") {
    return scope.customDateFrom && scope.customDateTo
      ? `${scope.customDateFrom} → ${scope.customDateTo}`
      : "Custom range";
  }
  return DATE_FILTER_LABELS[scope.dateFilter] ?? scope.dateFilter;
}

function ScopeChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-5 items-center rounded-md border border-border bg-muted/40 px-2 font-mono text-[10px] text-muted-foreground">
      {children}
    </span>
  );
}

function AnalyticsModalBody({
  initialScope,
}: {
  initialScope: AnalyticsScope;
}) {
  // State resets naturally: the body unmounts with the dialog.
  const [scope, setScope] = useState<AnalyticsScope>(initialScope);

  const login = useAccountInformationStore((s) => s.account?.login);
  const currency = useAccountInformationStore(
    (s) => s.account?.currency || "USD"
  );

  const analytics = useAccountAnalytics(scope, true);
  const positions = useFilterPositions({
    symbol: scope.symbol,
    dateFilter: scope.dateFilter,
    customDateFrom: scope.customDateFrom,
    customDateTo: scope.customDateTo,
  });

  const isAllScope =
    scope.symbol === "allsymbols" && scope.dateFilter === "all";

  if (!analytics) return null;

  return (
    <>
      {/* header */}
      <div className="flex h-9 shrink-0 items-center gap-2 border-b border-border px-3 pr-10">
        <DialogTitle className="text-[13px] font-semibold leading-none tracking-tight">
          Account Analytics{login ? ` — #${login}` : ""}
        </DialogTitle>
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <ScopeChip>
            {scope.symbol === "allsymbols" ? "All symbols" : scope.symbol}
          </ScopeChip>
          <ScopeChip>{scopeDateLabel(scope)}</ScopeChip>
          {analytics.firstTradeMs !== null && (
            <ScopeChip>
              {fmtDayMonth(analytics.firstTradeMs)} →{" "}
              {fmtDayMonth(analytics.lastTradeMs)}
            </ScopeChip>
          )}
          <ScopeChip>{analytics.tradeCount} trades</ScopeChip>
        </div>
        {!isAllScope && (
          <button
            type="button"
            className="ml-1 h-5 shrink-0 rounded-md border border-border px-2 font-mono text-[10px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            onClick={() => setScope({ symbol: "allsymbols", dateFilter: "all" })}
          >
            View all history
          </button>
        )}
      </div>

      {/* body */}
      <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
        {analytics.tradeCount === 0 ? (
          <div className="flex h-40 flex-col items-center justify-center gap-1 rounded-md border border-border bg-card text-muted-foreground">
            <p className="text-[12px]">No closed trades in this scope</p>
            <p className="text-[10px]">
              Analytics cover only the synced history window shown above.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2.5 xl:grid-cols-[280px_1fr]">
              <AnalyticsInfoCard analytics={analytics} />
              <AnalyticsChartCard analytics={analytics} currency={currency} />
            </div>
            <AnalyticsAdvancedCard analytics={analytics} currency={currency} />
            <AnalyticsActivityCard positions={positions} />
            <AnalyticsMonthlyCard
              analytics={analytics}
              positions={positions}
              currency={currency}
            />
            <p className="px-0.5 pb-1 text-[10px] text-muted-foreground">
              Computed client-side from the synced history window only —
              deposits, gain and drawdown are window-relative; drawdown is
              balance-based (closed trades). SL/TP tints are a tolerance-match
              heuristic.
            </p>
          </>
        )}
      </div>
    </>
  );
}
